import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Incident, type IncidentStatus } from '../models/Incident';
import { INCIDENT_LIST_STATUS_TAB_ORDER, type IncidentListStatusTab } from '../models/Filters';
import { useIncidents, UseIncidentsFilters } from './useIncidents';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { usePermissions } from '@shared/hooks/usePermissions';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const buildInitialSelectedColumns = (is3pl: boolean): string[] => [
  'order_ref',
  'shop_name',
  ...(is3pl ? ['shop_team_name'] : []),
  'tracking_number',
  'courier',
  'external_type',
  'status',
  'report_date',
  'resolution_date',
];

const FIXED_COLUMNS = [] as const;

const COLUMN_MAP: Record<string, string> = {
  order_ref: 'order_ref',
  shop_name: 'shop_name',
  shop_team_name: 'shop_team_name',
  tracking_number: 'tracking_number',
  external_type: 'external_type',
  status: 'status',
  report_date: 'report_date',
  resolution_date: 'resolution_date',
  shop_id: 'shop_id',
  team_id: 'team_id',
  courier_id: 'courier_id',
};

const FIELD_TO_API_SORT: Record<string, string> = {
  status: 'status',
  report_date: 'report_date',
  resolution_date: 'resolution_date',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

/**
 * Convierte las condiciones de filtro a los parámetros que acepta la API
 * La API solo acepta: status, courier_id, client_id, mapped, from, to, search
 */
const computeAdvancedFilters = (
  conditions: FilterConditionOutput[],
  searchValue: string,
): { filters: any[]; logic: 'AND' | 'OR' } | null => {
  if (!conditions || conditions.length === 0) {
    // Si no hay condiciones pero hay search, crear filtro para search
    return searchValue
      ? {
          filters: [
            { column: 'order_id', operator: 'contains', value: searchValue },
            { column: 'external_code', operator: 'contains', value: searchValue },
          ],
          logic: 'OR',
        }
      : null;
  }

  // Convertir condiciones a filtros con operador "equals" por defecto
  // ya que la API solo acepta query parameters simples
  const mappedFilters = conditions
    .map((condition) => {
      const apiColumn = COLUMN_MAP[condition.column];
      if (!apiColumn) {
        return null;
      }

      // Para la API, usamos operador "equals" por defecto
      // Los operadores complejos se simplifican
      let operator = 'equals';
      let value = condition.value;

      // Manejar diferentes operadores
      if (['in', 'not_in'].includes(condition.action)) {
        operator = condition.action;
        value = Array.isArray(condition.value) ? condition.value : [condition.value];
      } else if (['is_null', 'is_not_null'].includes(condition.action)) {
        operator = condition.action;
        value = undefined;
      } else if (['contains', 'not_contains'].includes(condition.action)) {
        // Para contains, usar equals (la API buscará coincidencias)
        operator = 'equals';
        value = condition.value;
      } else {
        operator = condition.action;
        value = condition.value;
      }

      const filter: Record<string, unknown> = {
        column: apiColumn,
        operator,
      };

      if (value !== undefined) {
        filter.value = value;
      }

      return filter;
    })
    .filter(Boolean) as any[];

  if (mappedFilters.length > 0) {
    return {
      filters: mappedFilters,
      logic: 'AND',
    };
  }

  return searchValue
    ? {
        filters: [
          { column: 'order_id', operator: 'contains', value: searchValue },
          { column: 'external_code', operator: 'contains', value: searchValue },
        ],
        logic: 'OR',
      }
    : null;
};

export const useIncidentsMain = () => {
  const { getTeamType } = usePermissions();
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'report_date' as string },
    sortDirection: { type: 'string', default: 'desc' as string },
    status: { type: 'array', default: [] as string[] },
    shop_ids: { type: 'string', default: '' as string },
    team_ids: { type: 'string', default: '' as string },
    courier_ids: { type: 'string', default: '' as string },
    mapped: { type: 'boolean', default: undefined },
    from: { type: 'string', default: '' },
    to: { type: 'string', default: '' },
    include_resolved_by_courier: { type: 'boolean', default: undefined },
  });

  const initialConditions = parseFiltersFromParams(queryParams.filters);

  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [shopIds, setShopIds] = useState<string[]>(() => {
    if (queryParams.shop_ids) {
      return queryParams.shop_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [teamIds, setTeamIds] = useState<string[]>(() => {
    if (queryParams.team_ids) {
      return queryParams.team_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(initialConditions);
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(initialConditions));
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = queryParams.sortBy || 'report_date';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return {
      field: sortField,
      direction,
    };
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() =>
    buildInitialSelectedColumns(getTeamType() === 'tenant_3pl'),
  );
  const [cachedIncidents, setCachedIncidents] = useState<Incident[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);

  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    if (queryParams.shop_ids) {
      setShopIds(queryParams.shop_ids.split(',').filter(Boolean));
    } else {
      setShopIds([]);
    }
  }, [queryParams.shop_ids]);

  useEffect(() => {
    if (queryParams.team_ids) {
      setTeamIds(queryParams.team_ids.split(',').filter(Boolean));
    } else {
      setTeamIds([]);
    }
  }, [queryParams.team_ids]);

  const [courierIds, setCourierIds] = useState<string[]>(() => {
    if (queryParams.courier_ids) {
      return queryParams.courier_ids.split(',').filter(Boolean);
    }
    return [];
  });

  useEffect(() => {
    if (queryParams.courier_ids) {
      setCourierIds(queryParams.courier_ids.split(',').filter(Boolean));
    } else {
      setCourierIds([]);
    }
  }, [queryParams.courier_ids]);

  useEffect(() => {
    const sortField = queryParams.sortBy || 'report_date';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const advancedFilters = useMemo(
    () => computeAdvancedFilters(filterConditions, searchTerm),
    [filterConditions, searchTerm],
  );

  const processedFilters = useMemo(() => {
    const apiField = sorting?.field ? FIELD_TO_API_SORT[sorting.field] || sorting.field : 'report_date';
    const apiDirection = sorting?.direction ?? 'desc';

    return {
      sortBy: apiField,
      sortDirection: apiDirection,
    };
  }, [sorting]);

  const listStatusFilter = useMemo((): IncidentStatus[] | undefined => {
    const raw = queryParams.status;
    if (!Array.isArray(raw) || raw.length === 0) {
      return undefined;
    }
    const statuses = raw.filter((s): s is IncidentStatus =>
      (INCIDENT_LIST_STATUS_TAB_ORDER as readonly string[]).includes(s),
    );
    return statuses.length > 0 ? statuses : undefined;
  }, [queryParams.status]);

  const queryFilters = useMemo(() => {
    const baseFilters: UseIncidentsFilters = {
      ...processedFilters,
      ...(advancedFilters ? { advanced_filters: advancedFilters } : {}),
      status: listStatusFilter,
      courier_ids: (courierIds?.length ?? 0) > 0 ? courierIds.join(',') : undefined,
      shop_ids: (shopIds?.length ?? 0) > 0 ? shopIds.join(',') : undefined,
      team_ids: (teamIds?.length ?? 0) > 0 ? teamIds.join(',') : undefined,
      mapped: queryParams.mapped,
      from: queryParams.from || undefined,
      to: queryParams.to || undefined,
      // Solo incluir include_resolved_by_courier si se solicita explícitamente
      include_resolved_by_courier:
        queryParams.include_resolved_by_courier !== undefined ? queryParams.include_resolved_by_courier : undefined,
    };

    if (searchTerm) {
      baseFilters.search = searchTerm;
    }

    return baseFilters;
  }, [
    processedFilters,
    advancedFilters,
    searchTerm,
    listStatusFilter,
    courierIds,
    shopIds,
    teamIds,
    queryParams.mapped,
    queryParams.from,
    queryParams.to,
    queryParams.include_resolved_by_courier,
  ]);

  /** Mismos criterios que el listado pero sin `status` (los contadores por pestaña fijan uno cada vez). */
  const queryFiltersBaseForStatusCounts = useMemo(() => {
    const base: UseIncidentsFilters = { ...queryFilters };
    delete (base as { status?: unknown }).status;

    if (base.advanced_filters?.filters?.length) {
      const filtersNoStatus = base.advanced_filters.filters.filter((f) => f.column !== 'status');
      if (filtersNoStatus.length > 0) {
        base.advanced_filters = { ...base.advanced_filters, filters: filtersNoStatus };
      } else {
        delete base.advanced_filters;
      }
    }
    return base;
  }, [queryFilters]);

  const {
    incidents: fetchedIncidents,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useIncidents(queryFilters);

  useEffect(() => {
    if (fetchedIncidents.length > 0) {
      setCachedIncidents(fetchedIncidents);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedIncidents, fetchedTotal]);

  const handleSearch = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      updateParam('search', trimmedTerm);
    },
    [updateParam],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const activeStatusTab = useMemo((): IncidentListStatusTab => {
    const raw = queryParams.status;
    if (!Array.isArray(raw) || raw.length === 0) {
      return 'all';
    }
    const first = raw[0] as string;
    if ((INCIDENT_LIST_STATUS_TAB_ORDER as readonly string[]).includes(first)) {
      return first as IncidentStatus;
    }
    return 'all';
  }, [queryParams.status]);

  const handleStatusTabChange = useCallback(
    (newStatus: IncidentListStatusTab) => {
      const withoutStatus = filterConditions.filter((c) => c.column !== 'status');
      const filtersChanged = withoutStatus.length !== filterConditions.length;
      const nextStatusParam = newStatus === 'all' ? [] : [newStatus];

      if (filtersChanged) {
        const serialized = serializeConditions(withoutStatus);
        filterConditionsSerializedRef.current = serialized;
        setFilterConditions(withoutStatus);
        updateParams({ status: nextStatusParam, filters: withoutStatus });
      } else {
        updateParam('status', nextStatusParam);
      }
    },
    [filterConditions, updateParam, updateParams],
  );

  const handleShopIdsChange = useCallback(
    (ids: string[]) => {
      setShopIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('shop_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleTeamIdsChange = useCallback(
    (ids: string[]) => {
      setTeamIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('team_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleCourierChange = useCallback(
    (ids: string[]) => {
      setCourierIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('courier_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  const handleClearAll = useCallback(() => {
    updateParams({
      search: '',
      filters: [],
      shop_ids: '',
      team_ids: '',
      courier_ids: '',
      mapped: undefined,
      from: '',
      to: '',
      include_resolved_by_courier: undefined,
    });
  }, [updateParams]);

  // 🎯 Detectar si hay filtros activos (excluyendo sorting por defecto)
  const hasActiveFilters = useMemo(
    () =>
      filterConditions.length > 0 ||
      Boolean(searchTerm.trim()) ||
      (Array.isArray(queryParams.status) && queryParams.status.length > 0) ||
      Boolean(courierIds?.length) ||
      queryParams.mapped !== undefined ||
      Boolean(queryParams.from) ||
      Boolean(queryParams.to) ||
      queryParams.include_resolved_by_courier === true,
    [
      filterConditions.length,
      searchTerm,
      queryParams.status,
      courierIds,
      queryParams.mapped,
      queryParams.from,
      queryParams.to,
      queryParams.include_resolved_by_courier,
    ],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedIncidents.length === 0 && cachedTotal > 0;

  const incidents = shouldUseCachedData ? cachedIncidents : fetchedIncidents;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const incidentsViewProps = useMemo(
    () => ({
      incidents,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [incidents, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const exportColumns = useMemo(
    () => selectedColumns.map((columnId) => COLUMN_MAP[columnId]).filter(Boolean) as string[],
    [selectedColumns],
  );

  return {
    incidents,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    searchTerm,
    shopIds,
    teamIds,
    courierIds,
    incidentsViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    filterConditions,
    handleFilterChange,
    handleSearch,
    handleShopIdsChange,
    handleTeamIdsChange,
    handleCourierChange,
    handleClearAll,
    handleColumnsChange,
    handleSortingChange,
    activeStatusTab,
    handleStatusTabChange,
    queryFiltersBaseForStatusCounts,
  };
};
